/**
 * Iframe-side runtime for the preview iframe.
 *
 * Lives *inside* the preview iframe. The parent posts the tree (and
 * tokens, viewport, selection) over postMessage; this runtime
 * compiles the tree to HTML, morphs it into the live document, and
 * reports clicks back to the parent.
 *
 * Runs as a regular module — no React, no big deps. Just morphdom and
 * the local compiler. We import morphdom from npm (will be bundled in
 * the iframe entry point); same dependency that powers the published
 * page's runtime when we get there.
 *
 * Lifecycle:
 *
 *   1. Module load → `bootIframeRuntime()` is called by the iframe's
 *      bootstrap script. It registers the message listener and posts
 *      `{ type: 'ready' }` upward.
 *   2. Parent posts `tree-update` → runtime calls `compileTreeToHtml`,
 *      morphs the result onto `document.body` (preserving focus and
 *      animation state), records the latest tree.
 *   3. Parent posts `set-tokens` → runtime updates a `<style>` element
 *      in `<head>` that holds the `:root { --oj-*: ... }` rule.
 *   4. Parent posts `set-selection` → runtime positions an outline
 *      overlay over the corresponding `[data-oj-id]` element.
 *   5. Parent posts `set-viewport` → runtime swaps a body class so the
 *      CSS responsive variants in scoped style blocks fire.
 *   6. User clicks anywhere in the document → runtime walks up to the
 *      closest `[data-oj-id]` and posts `{ type: 'select', nodeId }`
 *      back. The parent decides what to do with it.
 *
 * Origin policy: every parent message must come from `window.parent`.
 * Mismatched sources are dropped. The parent symmetrically validates
 * `event.source` and `event.origin`.
 *
 * The runtime is exposed as a namespace via `window.__ojIframeRuntime`
 * so tests can poke at it without re-mounting an iframe.
 */

import morphdom from 'morphdom';
import {
  PROTOCOL_VERSION,
  isProtocolMessage,
  type IframeToParentMessage,
  type ParentToIframeMessage,
  type SetTokensMessage,
} from './protocol';
import { compileTreeToHtml } from '../render/toHtml';
import { parseLandingPageData } from '../serialization';
import { registerOjElements } from './ojElements';
import type { LandingPageData, NodeId } from '../types';

// ─────────────────────────────────────────────────────────────────────────
// Public types — exposed for tests + parent-side type imports
// ─────────────────────────────────────────────────────────────────────────

/** Version stamp posted in the `ready` message. Bump when there's a
 *  runtime behavior change so the parent can opt to reload (Phase 3
 *  hot-reload). */
export const IFRAME_RUNTIME_VERSION = '1.0.0';

export interface BootOptions {
  /** Window the runtime listens for messages on (defaults to the
   *  global `window`). Tests pass a stub. */
  win?: Window & typeof globalThis;
  /** Document the runtime renders into (defaults to the global
   *  `document`). Tests pass a stub. */
  doc?: Document;
  /** Posting target — typically `window.parent`. Tests pass a stub. */
  parentWindow?: Window;
}

// ─────────────────────────────────────────────────────────────────────────
// Boot
// ─────────────────────────────────────────────────────────────────────────

/**
 * Wire up message handling and click reporting. Call once when the
 * iframe's bootstrap script loads. Returns a teardown function that
 * removes listeners (used by tests; the iframe itself never tears
 * down).
 */
export function bootIframeRuntime(opts: BootOptions = {}): () => void {
  const win = opts.win ?? (window as Window & typeof globalThis);
  const doc = opts.doc ?? document;
  const parentWindow = opts.parentWindow ?? win.parent;

  let lastTree: LandingPageData | null = null;
  let selectedId: NodeId | null = null;
  // Token style block lives in <head>; we keep a reference so updates
  // mutate this same node rather than appending duplicates.
  let tokenStyleNode: HTMLStyleElement | null = null;
  let viewport: 'desktop' | 'tablet' | 'mobile' = 'desktop';
  // Parent origin captured from the first inbound message. Used as
  // `targetOrigin` on every postToParent call so a cross-origin
  // navigation in the parent (rare but possible) doesn't leak the
  // tree state to whatever document occupies the parent window now.
  // Defaults to `null` until the first message arrives; postToParent
  // falls back to `'*'` only on the initial `ready` event before any
  // parent message has confirmed an origin.
  let capturedParentOrigin: string | null = null;

  // ─── Message handler ─────────────────────────────────────────────
  const onMessage = (event: MessageEvent) => {
    if (event.source !== parentWindow) return;
    if (!isProtocolMessage(event.data)) return;
    // Capture parent origin on the first valid message. We keep it
    // pinned — a malicious parent navigation that subsequently sends
    // messages from a different origin would have a different
    // `event.source`, so the source-window equality check above
    // protects us regardless. The captured origin is used as the
    // targetOrigin on the way back so leaks during a parent
    // navigation race close cleanly.
    if (capturedParentOrigin === null && event.origin && event.origin !== 'null') {
      capturedParentOrigin = event.origin;
    }

    const msg = event.data as ParentToIframeMessage;
    try {
      handleMessage(msg);
    } catch (err) {
      postToParent({
        protocol: PROTOCOL_VERSION,
        type: 'error',
        code: 'unknown',
        detail: err instanceof Error ? err.message : String(err),
      });
    }
  };

  function handleMessage(msg: ParentToIframeMessage) {
    switch (msg.type) {
      case 'tree-update': {
        const parsed = parseLandingPageData(msg.tree);
        if (!parsed.ok) {
          postToParent({
            protocol: PROTOCOL_VERSION,
            type: 'error',
            code: 'compile-failed',
            detail: `Tree parse failed: ${parsed.message} (${parsed.path ?? '<no path>'})`,
          });
          return;
        }
        lastTree = parsed.data;
        if (msg.tokens) applyTokens(msg.tokens);
        renderAndMorph();
        repaintSelection();
        break;
      }
      case 'set-selection': {
        selectedId = msg.nodeId;
        repaintSelection();
        break;
      }
      case 'set-tokens': {
        applyTokens(msg.tokens);
        break;
      }
      case 'set-viewport': {
        viewport = msg.viewport;
        applyViewport();
        break;
      }
      default: {
        const _exhaustive: never = msg;
        void _exhaustive;
      }
    }
  }

  // ─── Render + morph ──────────────────────────────────────────────
  function renderAndMorph() {
    if (!lastTree || !doc.body) return;
    let html: string;
    try {
      html = compileTreeToHtml(lastTree);
    } catch (err) {
      postToParent({
        protocol: PROTOCOL_VERSION,
        type: 'error',
        code: 'compile-failed',
        detail: err instanceof Error ? err.message : String(err),
      });
      return;
    }
    // Wrap in a single root div so morphdom has a stable target. The
    // root carries `data-oj-root` so click delegation can short-circuit.
    const wrappedHtml = `<div data-oj-root="true">${html}${selectionOverlayHtml(selectedId)}</div>`;
    const root = doc.body.querySelector('[data-oj-root="true"]') as HTMLElement | null;
    if (!root) {
      doc.body.innerHTML = wrappedHtml;
      return;
    }
    try {
      morphdom(root, wrappedHtml, {
        // Don't morph attributes that React-style frameworks normally
        // own; we do own them but the option list keeps us aligned with
        // morphdom's defaults for safe upgrade.
      });
    } catch (err) {
      postToParent({
        protocol: PROTOCOL_VERSION,
        type: 'error',
        code: 'morph-failed',
        detail: err instanceof Error ? err.message : String(err),
      });
    }
  }

  // ─── Tokens ──────────────────────────────────────────────────────
  function applyTokens(tokens: SetTokensMessage['tokens']) {
    if (!doc.head) return;
    if (!tokenStyleNode) {
      tokenStyleNode = doc.createElement('style');
      tokenStyleNode.setAttribute('data-oj-tokens', 'true');
      doc.head.appendChild(tokenStyleNode);
    }
    const lines: string[] = [];
    for (const [id, value] of Object.entries(tokens)) {
      if (typeof value !== 'string' || !value) continue;
      lines.push(`  --${cssEscapeIdentifier(id)}: ${cssEscapeValue(value)};`);
    }
    tokenStyleNode.textContent = `:root {\n${lines.join('\n')}\n}`;
  }

  // ─── Viewport ────────────────────────────────────────────────────
  function applyViewport() {
    if (!doc.body) return;
    doc.body.classList.remove('oj-viewport-desktop', 'oj-viewport-tablet', 'oj-viewport-mobile');
    doc.body.classList.add(`oj-viewport-${viewport}`);
    // Set a max-width on the root so responsive primitives hit their
    // breakpoints. Mobile = 375px, tablet = 768px, desktop = full.
    const root = doc.body.querySelector('[data-oj-root="true"]') as HTMLElement | null;
    if (!root) return;
    if (viewport === 'mobile') root.style.maxWidth = '375px';
    else if (viewport === 'tablet') root.style.maxWidth = '768px';
    else root.style.maxWidth = '';
    root.style.marginInline = viewport === 'desktop' ? '' : 'auto';
  }

  // ─── Selection overlay ───────────────────────────────────────────
  function repaintSelection() {
    if (!doc.body) return;
    const root = doc.body.querySelector('[data-oj-root="true"]') as HTMLElement | null;
    if (!root) return;
    const overlay = root.querySelector('[data-oj-overlay="true"]') as HTMLElement | null;
    const target = selectedId
      ? (root.querySelector(`[data-oj-id="${cssEscapeAttr(selectedId)}"]`) as HTMLElement | null)
      : null;
    if (!target) {
      if (overlay) overlay.style.display = 'none';
      return;
    }
    const targetRect = target.getBoundingClientRect();
    const rootRect = root.getBoundingClientRect();
    if (overlay) {
      overlay.style.display = 'block';
      overlay.style.left = `${targetRect.left - rootRect.left}px`;
      overlay.style.top = `${targetRect.top - rootRect.top}px`;
      overlay.style.width = `${targetRect.width}px`;
      overlay.style.height = `${targetRect.height}px`;
    }
  }

  // ─── Click reporting ─────────────────────────────────────────────
  // Capture-phase so we run before any default action (e.g. <a> nav).
  // We swallow the event when it lands on a tracked node so the
  // editor preview doesn't wander off when a customer clicks a link.
  const onClick = (event: MouseEvent) => {
    const target = event.target as HTMLElement | null;
    if (!target) return;
    const node = target.closest('[data-oj-id]') as HTMLElement | null;
    if (!node) {
      postToParent({ protocol: PROTOCOL_VERSION, type: 'select', nodeId: null });
      return;
    }
    const id = node.getAttribute('data-oj-id');
    event.preventDefault();
    event.stopPropagation();
    postToParent({ protocol: PROTOCOL_VERSION, type: 'select', nodeId: id });
  };

  // ─── Wire ────────────────────────────────────────────────────────
  win.addEventListener('message', onMessage);
  doc.addEventListener('click', onClick, true);

  // Register `<oj-*>` Custom Elements so the preview's compiled HTML
  // (which emits real `<oj-pay>` / `<oj-link-account-button>` tags)
  // paints labelled placeholders matching the React preview. The
  // same script ships on published pages with its connectedCallback
  // upgraded to fire real SDK actions — Phase 3 work.
  if (typeof win.customElements !== 'undefined') {
    registerOjElements({ registry: win.customElements });
  }

  // Announce ready. Parent only starts pushing tree-updates after this.
  postToParent({
    protocol: PROTOCOL_VERSION,
    type: 'ready',
    runtimeVersion: IFRAME_RUNTIME_VERSION,
  });

  // Expose for tests + diagnostics.
   
  (win as any).__ojIframeRuntime = {
    getLastTree: () => lastTree,
    getSelectedId: () => selectedId,
    getViewport: () => viewport,
    rerender: renderAndMorph,
  };

  // ─── Helpers bound to closure ────────────────────────────────────
  function postToParent(msg: IframeToParentMessage) {
    if (!parentWindow) return;
    // Use the captured parent origin once we've seen one. The initial
    // `ready` event fires before any parent message — fall back to
    // `'*'` for that single case so the parent receives the ready and
    // can start sending tree-update messages. Every subsequent post
    // uses the strict origin.
    parentWindow.postMessage(msg, capturedParentOrigin ?? '*');
  }

  // Teardown — used by tests to swap iframes; the iframe itself never
  // calls this in production.
  return () => {
    win.removeEventListener('message', onMessage);
    doc.removeEventListener('click', onClick, true);
  };
}

// ─────────────────────────────────────────────────────────────────────────
// Selection overlay markup
// ─────────────────────────────────────────────────────────────────────────

/** The overlay is a single positioned div the runtime moves around.
 *  Always emitted (display:none when nothing is selected) so morphdom
 *  doesn't have to add/remove it on each tick. */
function selectionOverlayHtml(_selectedId: NodeId | null): string {
  return `<div data-oj-overlay="true" aria-hidden="true" style="position:absolute;display:none;pointer-events:none;outline:2px solid #8b5cf6;outline-offset:-2px;z-index:9999"></div>`;
}

// ─────────────────────────────────────────────────────────────────────────
// CSS escaping (for tokens + selectors)
// ─────────────────────────────────────────────────────────────────────────

function cssEscapeIdentifier(value: string): string {
  // CSS custom-property names must match `--[A-Za-z_][A-Za-z0-9_-]*`.
  // The token taxonomy is closed and contains only ascii lowercase +
  // hyphens; this is defense-in-depth in case a malformed key slips
  // through.
  return value.replace(/[^a-zA-Z0-9_-]/g, '_');
}

function cssEscapeValue(value: string): string {
  // Match the cssVarStyle.ts escape — block `}` and the CSS comment
  // terminator so a hostile value can't break out of the rule.
  return value.replace(/\}|\*\//g, (m) => (m === '}' ? '\\7d ' : '*\\2f '));
}

function cssEscapeAttr(value: string): string {
  // Mirrors the editor canvas's allowlist — safe selector escape.
  return value.replace(/[^a-zA-Z0-9_-]/g, (ch) => `\\${ch}`);
}
